import React, { useMemo, useRef, useState } from 'react';
import { Modal, Table, Input, Select, message, Alert, Space } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useFetchCompany } from '../../../back-office/Companies/hooks/useFetch';
import Form from './Form';
import { useFetchTransferableUsers, useAddUserToCompany } from '../hooks/useFetch';

/**
 * เพิ่มพนักงานจากบริษัทอื่นเข้าบริษัทนี้
 *
 * ไม่ได้สร้างบัญชีใหม่ — เป็นการ "ผูก" บัญชีเดิมเข้าอีกบริษัท
 * เจ้าตัวใช้ชื่อ/รหัส/กะ/หน้าที่ ชุดเดิมทุกอย่าง แก้ที่ไหนก็มีผลทุกบริษัท
 *
 * รายชื่อที่ขึ้นตัด Admin(Input) ออกแล้วตั้งแต่ฝั่ง backend
 */
const AddFromOtherCompany = ({ open, onClose, onAdded }) => {
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState(null);
  const { data: allCompanies = [] } = useFetchCompany();
  const [addingId, setAddingId] = useState(null);
  const { data: users = [], isLoading, refetch, error } = useFetchTransferableUsers(open);
  const { mutateAsync: addUser } = useAddUserToCompany();

  // ตัวเลือกในดรอปดาวน์เอาเฉพาะบริษัทที่ "มีคนให้เพิ่มจริง" — เลือกแล้วไม่มีทางได้ตารางว่าง
  const companyOptions = useMemo(() => {
    const present = new Set();
    users.forEach((u) => {
      String(u.currentCompanyIds || '')
        .split(',')
        .filter(Boolean)
        .forEach((id) => present.add(String(id).trim()));
    });
    return (allCompanies || [])
      .filter((c) => present.has(String(c.id)))
      .map((c) => ({ value: String(c.id), label: c.companyName }));
  }, [users, allCompanies]);

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return users.filter((u) => {
      if (companyFilter) {
        const ids = String(u.currentCompanyIds || '').split(',').map((x) => x.trim());
        if (!ids.includes(String(companyFilter))) return false;
      }
      if (!keyword) return true;
      // เดิมค้นจาก homeCompanyName ซึ่งไม่มีแล้วหลังรื้อโครง → ค้นชื่อบริษัทไม่เจอเลย
      return (
        String(u.UserName || '').toLowerCase().includes(keyword) ||
        String(u.currentCompanies || '').toLowerCase().includes(keyword)
      );
    });
  }, [users, search, companyFilter]);

  // ไม่เพิ่มทันที — ต้องตั้งกะ/หน้าที่ของ "บริษัทนี้" ก่อน
  // จุดสำคัญของโครงนี้คือคนเดียวเป็นพนักงานคนละแบบในแต่ละบริษัทได้
  // ถ้าเพิ่มโดยไม่ตั้งค่า จะได้พนักงานที่ไม่มีกะ → ระบบจ่ายเคสให้ไม่ได้เลย
  const [picked, setPicked] = useState(null);
  const formRef = useRef(null);

  const handleConfirm = async () => {
    const values = await formRef.current.validateFields();
    setAddingId(picked.id);
    try {
      await addUser({ id: picked.id, ...values });
      message.success(`เพิ่ม ${picked.UserName} เป็นพนักงานของบริษัทนี้แล้ว`);
      setPicked(null);
      await refetch();
      onAdded?.();
    } catch (error) {
      message.error(error?.response?.data?.message || 'เพิ่มพนักงานไม่สำเร็จ');
    } finally {
      setAddingId(null);
    }
  };

  const columns = [
    { title: 'ชื่อผู้ใช้', dataIndex: 'UserName', key: 'UserName' },
    {
      title: 'ตอนนี้ทำงานที่',
      dataIndex: 'currentCompanies',
      key: 'currentCompanies',
      render: (names) => names || '-',
    },
    {
      title: '',
      key: 'action',
      align: 'center',
      render: (record) => (
        <span className="text-blue-500 cursor-pointer" onClick={() => setPicked(record)}>
          เพิ่มเข้าบริษัทนี้
        </span>
      ),
    },
  ];

  return (
    <Modal
      title="เพิ่มพนักงานจากบริษัทอื่น"
      open={open}
      onCancel={onClose}
      footer={null}
      width={860}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* แยกให้ชัดว่า "โหลดไม่ได้" ไม่ใช่ "ไม่มีคนให้เพิ่ม" */}
        {error && (
          <Alert
            type="error"
            showIcon
            message="โหลดรายชื่อไม่สำเร็จ"
            description={error?.response?.data?.message || error?.message || 'เกิดข้อผิดพลาดกับเซิร์ฟเวอร์'}
          />
        )}

        <Alert
          type="info"
          showIcon
          message="เป็นการให้สิทธิ์บัญชีเดิม ไม่ใช่สร้างบัญชีใหม่"
          description="พนักงานใช้ชื่อผู้ใช้และรหัสผ่านเดิม เข้าทำงานได้ทั้งสองบริษัท · แก้ข้อมูลที่ไหนก็มีผลทุกบริษัท · ตำแหน่ง Admin(Input) ใช้ข้ามบริษัทไม่ได้ จึงไม่แสดงในรายการนี้"
        />

        <Space.Compact style={{ width: '100%' }}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 260 }}
            placeholder="ทุกบริษัท"
            value={companyFilter}
            onChange={(v) => setCompanyFilter(v ?? null)}
            options={companyOptions}
          />
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="ค้นหาชื่อผู้ใช้"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Space.Compact>

        <Table
          rowKey="id"
          size="small"
          loading={isLoading}
          dataSource={filtered}
          columns={columns}
          pagination={{ pageSize: 8 }}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: error ? 'โหลดไม่สำเร็จ' : 'ไม่มีพนักงานจากบริษัทอื่นให้เพิ่ม' }}
          title={() => `พบ ${filtered.length} คน${companyFilter || search ? ` (จากทั้งหมด ${users.length})` : ''}`}
        />
      </Space>

      {/* ตั้งกะ/หน้าที่สำหรับบริษัทนี้โดยเฉพาะ — ใช้ฟอร์มเดียวกับเพิ่มพนักงานใหม่
          ต่างกันแค่ไม่ต้องกรอกชื่อผู้ใช้/รหัสผ่าน เพราะใช้บัญชีเดิม */}
      <Modal
        title={`ตั้งค่าการทำงานของ ${picked?.UserName ?? ''} ในบริษัทนี้`}
        open={!!picked}
        onCancel={() => setPicked(null)}
        onOk={handleConfirm}
        okText="เพิ่มเข้าบริษัท"
        cancelText="ยกเลิก"
        confirmLoading={addingId === picked?.id}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message="ใช้ login เดิม แต่กะและหน้าที่เป็นของบริษัทนี้แยกต่างหาก"
          description="ตั้งคนละกะกับบริษัทอื่นได้ · ปิดใช้งานที่นี่ บริษัทอื่นไม่กระทบ"
        />
        <Form formRef={formRef} initialValues={{ enable: true }} hideAccountFields />
      </Modal>
    </Modal>
  );
};

export default AddFromOtherCompany;
